import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowRight, MessageCircle, Landmark, FileSpreadsheet, FileText,
  Users, ReceiptText, ChevronRight,
} from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import RelatedLinks from '../components/RelatedLinks';

const INTEGRATIONS = [
  { icon: MessageCircle, title: 'WhatsApp', status: 'Available', desc: 'The core of MoneyFeed. Text income, expenses and receipts to your MoneyFeed number and they’re logged, categorized and kept tax-ready. Powered by the WhatsApp Business Platform via Twilio.', to: '/whatsapp-bookkeeping' },
  { icon: Landmark, title: 'Bank connections', status: 'Coming soon', desc: 'Bank linking via Plaid is planned for a future release, so it is not available yet. When it arrives, you will be able to connect a bank or card account to auto-import transactions. Connecting will be entirely optional.', to: '/features' },
  { icon: FileSpreadsheet, title: 'CSV & Excel export', status: 'Available', desc: 'Export categorized income and expenses to a universal CSV or a real Excel (.xlsx) workbook — ready to import into QuickBooks, Xero, Google Sheets or any accounting tool.', to: '/export-to-excel' },
  { icon: FileText, title: 'PDF reports', status: 'Available', desc: 'Download categorized PDF reports and receipt binders with the original images attached — a complete audit trail for you or your accountant.', to: '/scan-receipts-to-pdf' },
  { icon: Users, title: 'Accountant workflow', status: 'Available', desc: 'Share clean, categorized, tax-ready data with your bookkeeper or CPA. Clients capture on WhatsApp; accountants export and file.', to: '/for-accountants' },
  { icon: ReceiptText, title: 'Invoice links', status: 'Available', desc: 'Create invoices and share a payment link on WhatsApp or by email. When an invoice is marked paid, the income is recorded automatically.', to: '/invoicing' },
];

const FAQS = [
  { q: 'What does MoneyFeed integrate with?', a: 'MoneyFeed works with WhatsApp for capturing income, expenses and receipts, bank connections via Plaid, which are planned for a future release and are not available yet, and CSV, Excel and PDF exports that import into tools like QuickBooks and Xero or go straight to your accountant. It also creates shareable invoice links.' },
  { q: 'Do I have to connect my bank?', a: 'No. Bank connection is completely optional, and it is not available yet — bank linking via Plaid is planned for a future release. Many users track everything just by texting on WhatsApp and scanning receipts. When bank linking arrives, connecting a bank will simply add automatic import.' },
  { q: 'Can I import MoneyFeed data into QuickBooks or Xero?', a: 'Yes. MoneyFeed exports clean CSV and Excel files with consistent columns (date, amount, currency, category, vendor, tax) that you can import into QuickBooks, Xero, Google Sheets or any spreadsheet-based workflow.' },
  { q: 'Which of these are available today?', a: 'WhatsApp capture, CSV/Excel/PDF export, the accountant export workflow and invoice links are available now. Bank connections are not available yet — bank linking via Plaid is planned for a future release.' },
];

export default function Integrations() {
  const jsonLd = [
    { '@context': 'https://schema.org', '@type': 'WebPage', name: 'MoneyFeed Integrations', url: 'https://moneyfeed.io/integrations', description: 'How MoneyFeed connects to your workflow: WhatsApp capture, CSV/Excel/PDF export, accountant handoff and shareable invoice links. Bank connections via Plaid are planned for a future release.' },
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#ECFDF5] to-[#D1FAE5]" data-testid="integrations-page">
      <Seo
        title="MoneyFeed Integrations — WhatsApp, Excel & Accountants"
        description="How MoneyFeed connects to your workflow: WhatsApp capture, CSV/Excel/PDF export to QuickBooks or Xero, accountant handoff and invoice links. Bank auto-import via Plaid is planned for a future release."
        path="/integrations"
        jsonLd={jsonLd}
      />
      <LandingHeader />

      <nav className="max-w-6xl mx-auto px-4 sm:px-8 pt-6 text-xs text-gray-500 flex items-center gap-1" aria-label="Breadcrumb" data-testid="integrations-breadcrumb">
        <Link to="/" className="hover:text-[#064E3B]">Home</Link>
        <ChevronRight className="h-3 w-3" /> <span className="text-[#064E3B] font-semibold">Integrations</span>
      </nav>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 pt-8 pb-6 text-center">
        <h1 className="text-4xl sm:text-5xl font-bold text-[#064E3B] leading-tight mb-5">Integrations & how MoneyFeed connects</h1>
        <p className="text-base sm:text-lg text-gray-700 max-w-2xl mx-auto">
          MoneyFeed fits into the tools you already use. Capture on WhatsApp and export clean data to spreadsheets, your accountant or accounting software. Bank auto-import is coming soon.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {INTEGRATIONS.map((it) => (
            <Link key={it.title} to={it.to} className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg hover:border-[#84CC16] border border-transparent transition-all" data-testid={`integration-${it.title}`}>
              <div className="w-11 h-11 rounded-xl bg-[#ECFDF5] flex items-center justify-center mb-4">
                <it.icon className="h-5 w-5 text-[#065F46]" />
              </div>
              <div className="flex items-center justify-between mb-1">
                <h2 className="font-bold text-[#064E3B]">{it.title}</h2>
                <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#84CC16]/20 text-[#4d7c0f]">{it.status}</span>
              </div>
              <p className="text-gray-600 text-sm">{it.desc}</p>
            </Link>
          ))}
        </div>
        <p className="text-center text-xs text-gray-400 mt-6 max-w-2xl mx-auto">
          Bank connections are coming soon; all other integrations listed are available today. MoneyFeed is not affiliated with WhatsApp, Meta, QuickBooks or Xero; those names are trademarks of their respective owners.
        </p>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">Integrations FAQ</h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-6" data-testid={`integrations-faq-${i}`}>
              <h3 className="font-bold text-[#064E3B] mb-2">{f.q}</h3>
              <p className="text-gray-600 text-sm">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <div className="bg-[#064E3B] rounded-3xl p-10 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Works with how you already work</h2>
          <p className="text-[#A7F3D0] mb-6 max-w-xl mx-auto">Start free on WhatsApp and export to your accountant or spreadsheet anytime.</p>
          <Link to="/auth">
            <Button className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-8 py-6 text-base" data-testid="integrations-cta-signup">
              Get started free <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      <RelatedLinks />

      <footer className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#064E3B]">Home</Link>
          <Link to="/features" className="hover:text-[#064E3B]">Features</Link>
          <Link to="/export-to-excel" className="hover:text-[#064E3B]">Export to Excel</Link>
          <Link to="/for-accountants" className="hover:text-[#064E3B]">For Accountants</Link>
          <Link to="/invoicing" className="hover:text-[#064E3B]">Invoicing</Link>
        </div>
      </footer>
    </div>
  );
}
